import { api } from './api'

export interface OrganizationPayload {
  name: string
  website?: string
  industry?: string
  company_size?: string
  description?: string
  city?: string
  is_public?: boolean
  target_markets?: string[]
  target_countries?: string[]
  outreach_from_email?: string
  outreach_call_to_action_link?: string
  business_category?: string
  ideal_customer_profile?: IdealCustomerProfile
  prospecting_goals?: string[]
  sample_list_query?: string
  // Optional: ID of a claimed public analysis to skip re-crawling the website
  claimed_analysis_id?: string
}

export interface IdealCustomerProfile {
  services_offered: string[]
  target_customer_industries: string[]
  target_company_types: string[]
  target_company_size: string[]
  value_proposition: string
  buyer_persona: string[]
  geographic_focus: string[]
}

export interface CompanyAnalysisRequest {
  website: string
  name?: string
  description?: string
}

export interface CompanyAnalysisResponse {
  suggested_industry: string[]
  suggested_target_markets: string[]
  suggested_target_countries: string[]
  suggested_business_category: string | null
  suggested_prospecting_goals: string[]
  suggested_name: string | null
  suggested_list_query: string | null
  confidence: number
  website_language: string | null
  // ICP extracted alongside the company profile (same crawl)
  ideal_customer_profile: IdealCustomerProfile | null
}

export const BUSINESS_CATEGORY_LABELS: Record<string, string> = {
  b2b_marketplace: 'B2B Marketplace',
  manufacturer: 'Manufacturer',
  trading_company: 'Trading Company',
  saas: 'SaaS / Technology',
  local_service: 'Local Service Business',
  distributor: 'Distributor',
  professional_services: 'Professional Services',
  other: 'Other',
}

export interface TeamMember {
  id: string
  organization_id: string
  user_id: string
  role: string
  status: string
  invited_by?: string | null
  joined_at?: string | null
  created_at?: string | null
  full_name?: string | null
  avatar_url?: string | null
}

export type InviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked'

export interface TeamInvite {
  id: string
  organization_id: string
  email: string | null
  role: string
  expires_at: string
  created_at?: string | null
  accepted_at?: string | null
  accepted_by?: string | null
  revoked_at?: string | null
  revoked_by?: string | null
  status: InviteStatus
  invite_token?: string
}

export interface TeamJoinRequest {
  id: string
  organization_id: string
  user_id: string
  message: string | null
  status: string
  created_at?: string | null
}

export interface TeamOverview {
  members: TeamMember[]
  invites: TeamInvite[]
  join_requests: TeamJoinRequest[]
}

export interface SetupStep {
  id: string
  label: string
  description: string
  completed: boolean
}

export interface SetupProgressData {
  steps: SetupStep[]
  completed_count: number
  total_count: number
  percentage: number
  all_done: boolean
}

export const organizationApi = {
  create: async (payload: OrganizationPayload) => {
    const response = await api.post('/v1/organizations', payload)
    return response.data
  },

  listPublic: async (q?: string) => {
    const response = await api.get('/v1/organizations', { params: { q } })
    return response.data.items || []
  },

  getBySlug: async (slug: string) => {
    const response = await api.get(`/v1/organizations/${slug}`)
    return response.data
  },

  update: async (organizationId: string, payload: Partial<OrganizationPayload>) => {
    const response = await api.put(`/v1/organizations/${organizationId}`, payload)
    return response.data
  },

  listMembers: async (organizationId: string) => {
    const response = await api.get(`/v1/organizations/${organizationId}/members`)
    return response.data.items || []
  },

  updateMemberRole: async (organizationId: string, userId: string, role: string) => {
    const response = await api.put(`/v1/organizations/${organizationId}/members/${userId}/role`, { role })
    return response.data
  },

  removeMember: async (organizationId: string, userId: string) => {
    await api.delete(`/v1/organizations/${organizationId}/members/${userId}`)
  },

  createInvite: async (
    organizationId: string,
    email?: string,
    role = 'member',
    expiresInDays = 7
  ) => {
    const response = await api.post(`/v1/organizations/${organizationId}/invites`, {
      email,
      role,
      expires_in_days: expiresInDays
    })
    return response.data.item
  },

  listInvites: async (organizationId: string) => {
    const response = await api.get(`/v1/organizations/${organizationId}/invites`)
    return response.data.items || []
  },

  deleteInvite: async (organizationId: string, inviteId: string) => {
    await api.delete(`/v1/organizations/${organizationId}/invites/${inviteId}`)
  },

  revokeInvite: async (organizationId: string, inviteId: string) => {
    await api.delete(`/v1/organizations/${organizationId}/invites/${inviteId}`)
  },

  getTeamOverview: async (organizationId: string): Promise<TeamOverview> => {
    const response = await api.get(`/v1/organizations/${organizationId}/team`)
    return response.data
  },

  getInviteByToken: async (token: string) => {
    const response = await api.get(`/v1/invites/${token}`)
    return response.data.item
  },

  acceptInvite: async (token: string) => {
    const response = await api.post(`/v1/invites/${token}/accept`)
    return response.data.item
  },

  createJoinRequest: async (organizationId: string, message?: string) => {
    const response = await api.post(`/v1/organizations/${organizationId}/join-requests`, { message })
    return response.data.item
  },

  listJoinRequests: async (organizationId: string) => {
    const response = await api.get(`/v1/organizations/${organizationId}/join-requests`)
    return response.data.items || []
  },

  approveJoinRequest: async (organizationId: string, requestId: string) => {
    const response = await api.post(`/v1/organizations/${organizationId}/join-requests/${requestId}/approve`)
    return response.data.item
  },

  rejectJoinRequest: async (organizationId: string, requestId: string) => {
    const response = await api.post(`/v1/organizations/${organizationId}/join-requests/${requestId}/reject`)
    return response.data.item
  },

  onboardingStatus: async () => {
    const response = await api.get('/v1/onboarding/status')
    return response.data
  },

  completeOnboarding: async (activeOrganizationId?: string) => {
    const response = await api.post('/v1/onboarding/complete', {
      active_organization_id: activeOrganizationId
    })
    return response.data.item
  },

  listMyOrganizations: async () => {
    const response = await api.get('/v1/me/organizations')
    return response.data
  },

  setActiveOrganization: async (organizationId: string) => {
    const response = await api.put('/v1/me/active-organization', {
      organization_id: organizationId
    })
    return response.data
  },

  // Website analysis for ICP extraction
  analyzeWebsite: async (organizationId: string) => {
    const response = await api.post(`/v1/organizations/${organizationId}/analyze-website`)
    return response.data
  },

  // Update ICP directly
  updateICP: async (organizationId: string, icp: IdealCustomerProfile) => {
    const response = await api.put(`/v1/organizations/${organizationId}`, {
      ideal_customer_profile: icp
    })
    return response.data
  },

  // Pre-creation company analysis for onboarding
  analyzeCompany: async (payload: CompanyAnalysisRequest): Promise<CompanyAnalysisResponse> => {
    const response = await api.post('/v1/onboarding/analyze-company', payload)
    return response.data
  },

  // Get setup progress for onboarding checklist
  getSetupProgress: async (): Promise<SetupProgressData> => {
    const response = await api.get('/v1/onboarding/setup-progress')
    return response.data
  },
}
